import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarCheck, ChevronLeft, ChevronRight, Clock3, Lock, Trophy } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { portalApi, portalErr, inr } from "./portalApi";
import { useBuy } from "./PortalStore";
import "../test/testTheme.css";

function ChallengeEngine({ game, onFinish }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(game.answers || {});
  const [left, setLeft] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const offsetRef = useRef(game.server_now * 1000 - Date.now());
  const submittedRef = useRef(false);

  const doSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await portalApi.post("/portal/challenge/submit", { attempt_id: game.attempt_id });
      onFinish(data.attempt_id);
    } catch { submittedRef.current = false; setSubmitting(false); setError("Could not submit. Check your connection and try again."); }
  };

  useEffect(() => {
    const tick = () => {
      const remaining = Math.max(0, Math.round((game.deadline * 1000 - (Date.now() + offsetRef.current)) / 1000));
      setLeft(remaining);
      if (remaining <= 0 && !submittedRef.current) { submittedRef.current = true; doSubmit(); }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const select = (qid, opt) => {
    setAnswers((c) => ({ ...c, [qid]: opt }));
    portalApi.post("/portal/challenge/answer", { attempt_id: game.attempt_id, question_id: qid, selected: opt }).catch(() => {});
  };

  const question = game.questions[idx];
  const answeredCount = Object.keys(answers).length;
  const mm = String(Math.floor((left ?? 0) / 60)).padStart(2, "0");
  const ss = String((left ?? 0) % 60).padStart(2, "0");

  return (
    <div className="tp-wrap" style={{ maxWidth: 820, paddingBottom: 60 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap", margin: "24px 0 14px" }}>
        <span className="tp-pill warn" data-testid="challenge-title-pill"><Trophy size={14} /> {game.title.toUpperCase()}</span>
        <span className={left !== null && left <= 60 ? "tp-timer low" : "tp-timer"} data-testid="challenge-timer"><Clock3 size={17} /> {mm}:{ss}</span>
        <span className="tp-pill info" data-testid="challenge-qcount">QUESTION {idx + 1} OF {game.questions.length}</span>
      </div>
      <div className="tp-progress"><i style={{ width: `${(answeredCount / game.questions.length) * 100}%` }} /></div>
      {error && <p className="tp-error" role="alert" data-testid="challenge-engine-error">{error}</p>}
      <div className="tp-qcard" data-testid="challenge-question-card" style={{ marginTop: 18 }}>
        <h3 data-testid="challenge-question-text">{question.text}</h3>
        {question.image_url && <img src={question.image_url} alt="Question diagram" />}
        {["A", "B", "C", "D"].map((key) => (
          <button type="button" key={key} className={answers[question.question_id] === key ? "tp-option selected" : "tp-option"} onClick={() => select(question.question_id, key)} data-testid={`challenge-option-${key}`}>
            <span className="key">{key}</span> {question.options[key]}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, gap: 12, flexWrap: "wrap" }}>
        <button className="tp-btn ghost small" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="challenge-prev"><ChevronLeft size={16} /> Previous</button>
        <div className="tp-qnav">{game.questions.map((q, i) => <button type="button" key={q.question_id} className={`${answers[q.question_id] ? "answered" : ""} ${i === idx ? "current" : ""}`} onClick={() => setIdx(i)} data-testid={`challenge-nav-${i}`}>{i + 1}</button>)}</div>
        {idx < game.questions.length - 1
          ? <button className="tp-btn secondary small" onClick={() => setIdx(idx + 1)} data-testid="challenge-next">Next <ChevronRight size={16} /></button>
          : <button className="tp-btn small" onClick={() => setConfirming(true)} data-testid="challenge-submit-open">Submit</button>}
      </div>
      {confirming && <div className="tp-modal-backdrop" data-testid="challenge-confirm"><div className="tp-modal">
        <h3>Submit your challenge?</h3>
        <p className="tp-muted">One attempt only — {answeredCount} of {game.questions.length} answered. Your score goes on this month&apos;s leaderboard.</p>
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button className="tp-btn" style={{ flex: 1 }} onClick={() => { submittedRef.current = true; setConfirming(false); doSubmit(); }} disabled={submitting} data-testid="challenge-submit-confirm">{submitting ? "Submitting…" : "Yes, submit"}</button>
          <button className="tp-btn ghost" style={{ flex: 1 }} onClick={() => setConfirming(false)}>Keep answering</button>
        </div>
      </div></div>}
    </div>
  );
}

function ChallengeInner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [game, setGame] = useState(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const { buy, buying, error: buyError } = useBuy(() => { portalApi.get("/portal/challenge").then(({ data: d }) => setData(d)); });

  const load = () => portalApi.get("/portal/challenge").then(({ data: d }) => setData(d)).catch((err) => setError(portalErr(err)));
  useEffect(() => { load(); }, []);

  const start = async () => {
    setStarting(true);
    setError("");
    try {
      const { data: g } = await portalApi.post("/portal/challenge/start");
      setGame(g);
    } catch (err) { setError(portalErr(err)); } finally { setStarting(false); }
  };

  if (game) return <ChallengeEngine game={game} onFinish={(id) => navigate(`/portal/challenge/result/${id}`)} />;

  return (
    <div className="tp-wrap" style={{ maxWidth: 820, padding: "44px 22px 70px" }}>
      <div className="tp-eyebrow">WEEKLY PHYSICS CHALLENGE · {data?.month}</div>
      <h1 className="tp-h2" style={{ fontSize: 32 }} data-testid="challenge-page-title">One week. Five questions. Top the board.</h1>
      {error && <p className="tp-error" role="alert" data-testid="challenge-error">{error}</p>}
      {buyError && <p className="tp-error" role="alert">{buyError}</p>}
      {!data ? <p className="tp-muted">Loading…</p> : <>
        {!data.challenge ? (
          <div className="tp-panel" style={{ textAlign: "center" }} data-testid="challenge-none">
            <p className="tp-muted">No live challenge this moment.{data.upcoming ? ` Next one: "${data.upcoming.title}" starting ${data.upcoming.start_date}.` : " The next challenge is being prepared."}</p>
          </div>
        ) : (
          <div className="tp-reward tp-challenge-card" style={{ textAlign: "left" }} data-testid="challenge-card">
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <Trophy size={22} style={{ color: "#f77f00" }} />
              <h2 style={{ margin: 0, fontSize: 20 }} data-testid="challenge-name">{data.challenge.title}</h2>
              <span className="tp-pill info"><CalendarCheck size={13} /> {data.challenge.start_date} → {data.challenge.end_date}</span>
            </div>
            {data.challenge.prize_note && <p style={{ margin: "12px 0 4px" }} data-testid="challenge-prize"><b>Prize:</b> {data.challenge.prize_note}{data.challenge.winner_count > 1 ? ` (top ${data.challenge.winner_count} winners)` : ""}</p>}
            <p className="tp-muted" style={{ fontSize: 13 }} data-testid="challenge-rules"><b>Rules:</b> {data.rules}</p>
            {data.my_attempt?.status === "submitted" ? (
              <div style={{ marginTop: 14 }} data-testid="challenge-played">
                <p className="tp-pill ok">You scored {data.my_attempt.result.score}/{data.my_attempt.result.max_score} this week{data.my_rank ? ` · Rank #${data.my_rank}` : ""}</p>
                <Link className="tp-btn secondary small" to={`/portal/challenge/result/${data.my_attempt.attempt_id}`} data-testid="challenge-view-result">View answers & solutions</Link>
              </div>
            ) : data.entitled ? (
              <button className="tp-btn" style={{ marginTop: 14 }} disabled={starting} onClick={start} data-testid="challenge-enter">{starting ? "Preparing…" : "ENTER THIS WEEK'S CHALLENGE"}</button>
            ) : (
              <div style={{ marginTop: 14 }} data-testid="challenge-paywall">
                <p className="tp-muted">Entry needs the Weekly Challenge monthly pass — covers every challenge this month.</p>
                <button className="tp-btn" disabled={buying} onClick={() => buy("product", "weekly_challenge", "Weekly Physics Challenge — monthly pass")} data-testid="challenge-buy">
                  <Lock size={14} /> {buying ? "Opening…" : `GET THE MONTHLY PASS — ${inr(data.pass_price_paise)}`}
                </button>
              </div>
            )}
          </div>
        )}
        <section className="tp-panel" style={{ marginTop: 22 }} data-testid="challenge-leaderboard">
          <h2 className="tp-h2" style={{ fontSize: 20 }}>This month&apos;s leaderboard</h2>
          {data.leaderboard.length === 0 ? <p className="tp-muted" data-testid="challenge-board-empty">No entries yet — the first submitted challenge takes the top spot.</p> : (
            <div style={{ marginTop: 10 }}>
              {data.leaderboard.map((r) => (
                <div key={r.rank} className="tp-lb-row" style={r.rank === data.my_rank ? { background: "#fff6ea", borderRadius: 8 } : {}} data-testid={`challenge-lb-${r.rank}`}>
                  <span className={`tp-lb-rank rank-${r.rank}`}>{r.rank}</span>
                  <span className="tp-lb-name">{r.name}{r.rank === data.my_rank ? " (you)" : ""}<small>{r.challenges_played} challenge{r.challenges_played > 1 ? "s" : ""} played</small></span>
                  <span className="tp-lb-score">{r.score}/{r.max_score}<small>{r.avg_accuracy}% accuracy</small></span>
                </div>
              ))}
            </div>
          )}
        </section>
      </>}
    </div>
  );
}

export default function PortalChallenge() {
  return <PortalShell><ChallengeInner /></PortalShell>;
}
